<template>
    <div
        hotspot
        question
        :id="`question-${questionData.number}`"
        @keyup.enter="submitQuestion"
        ref="main"
    >
        <questionheader :questionData="questionData" :showImage="false" />
        <div data-question-main>
            <div data-options :style="`height: ${optionsHeight}px;`">
                <!-- The options that can be dragged on the main image -->
                <div
                    v-for="(option, index) in optionImages"
                    @mouseup="optionMouseUp(index, true, $event)"
                    @keydown="optionMouseUp(index, true, $event)"
                    @focus="optionMouseUp(index, false, $event)"
                    :tabindex="allAnswersGiven || !editingAllowed ? -1 : 0"
                    :key="`option-image-${index}`"
                    data-option
                    :data-selected="selectedImageIndex === index"
                >
                    <vue-drag-resize
                        v-if="showImages === false"
                        :ref="`option-image-${index}`"
                        :id="`question-${questionData.number}_option-image-${index}`"
                        :isDraggable="editingAllowed"
                        :disabled="allAnswersGiven || !editingAllowed"
                        :isResizable="false"
                        :y="option.y"
                        :x="option.x"
                        :w="option.width"
                        :h="option.height"
                        data-drag-option-image
                        :z="1"
                        @dragstop="optionDragStop(index)"
                        :data-option-type="option.type"
                    >
                        <media
                            v-if="option.type === `image`"
                            type="Image"
                            :alt="option.alt"
                            :url="`${state.surveyInfo.domain}/v1/${option.url}`"
                            :disabled="allAnswersGiven || !editingAllowed"
                            :style="option.extension === `.svg` ? `min-width:50px;` : ``"
                        />
                        <div
                            v-else-if="option.type === `text`"
                            :disabled="allAnswersGiven || !editingAllowed"
                        >
                            <p v-html="DOMPurify.sanitize(option.label)"></p>
                        </div>
                    </vue-drag-resize>
                </div>
            </div>
            <div data-image>
                <div data-image-area>
                    <!-- The main image on which the options are being dragged -->
                    <zoom-image
                        v-if="mainImage !== undefined"
                        :src="`${state.surveyInfo.domain}/v1/${mainImage.url}`"
                        :id="`question-${questionData.number}_media-image`"
                        :alt="mainImage.alt"
                        data-main-image
                        @element-clicked="mainImageMouseUp"
                        ref="main-image"
                        :showPopupOnClick="noAnswerIsSelected"
                    />
                    <!-- It takes some time before the dimensions of the mainImage are known, until then we don't want to show anything because the coordinate will be wrong -->
                    <template v-if="imageLoaded">
                        <!-- The answers that have already been given -->
                        <template v-for="(answer, index) in givenAnswers">
                            <vue-drag-resize
                                :isDraggable="editingAllowed"
                                :isResizable="false"
                                :w="getInitialWidthOfAnswer(answer).width"
                                :h="getInitialWidthOfAnswer(answer).height"
                                @clicked="givenAnswerDragStart(index)"
                                @dragstop="givenAnswerDragStop(index)"
                                :x="mainImageSize.x * (answer.percentX / 100)"
                                :y="mainImageSize.y * (answer.percentY / 100)"
                                :ref="`given-answer-drag-${index}`"
                                :id="`question-${questionData.number}_given-answer-drag-${index}`"
                                data-tab-given-answer
                                :z="1"
                                :parentLimitation="false"
                                :data-hidden="answer.isHidden"
                                :key="`givenAnswer-${index}`"
                            >
                                <media
                                    v-if="answer.media[0]"
                                    :tabindex="editingAllowed ? 0 : -1"
                                    type="Image"
                                    :alt="answer.media[0].alt"
                                    :url="`${state.surveyInfo.domain}/v1/${answer.media[0].url}`"
                                    :disabled="!editingAllowed"
                                    :ref="`given-answer-${index}`"
                                    @element-keydown="givenAnswerKeyUp($event, index)"
                                />
                                <p
                                    v-else
                                    :tabindex="editingAllowed ? 0 : -1"
                                    :ref="`given-answer-${index}`"
                                    v-html="DOMPurify.sanitize(answer.label)"
                                    :disabled="!editingAllowed"
                                    :id="`question-${questionData.number}_label-${index}`"
                                    @keydown="givenAnswerKeyUp($event, index)"
                                ></p>
                                <span
                                    data-remove-given-answer
                                    :tabindex="editingAllowed ? 0 : -1"
                                    :disabled="!editingAllowed"
                                    @click="removeGivenAnswer(index)"
                                    @touchend="removeGivenAnswer(index)"
                                    @keydown.space="removeGivenAnswer(index)"
                                    :id="`question-${questionData.number}_remove-answer-${index}`"
                                >
                                    <p
                                        data-only-screenreader
                                    >{{$store.getters.textResource(`rs_remove_answer`)}}</p>
                                    <font-awesome-icon :icon="`times-circle`" />
                                </span>
                            </vue-drag-resize>
                        </template>
                    </template>
                </div>
            </div>
        </div>
        <transition name="fade">
            <div v-if="showTextPopup" data-text-popup @click.self="nextClicked">
                <div data-prompt>
                    <span data-remove-popup @mouseup="nextClicked">
                        <font-awesome-icon :icon="`times-circle`" />
                    </span>
                    <header>
                        <strong>{{$store.getters.textResource(`rs_give_a_description`)}}</strong>
                    </header>
                    <main>
                        <div option-images-container>
                            <div
                                v-for="(option, index) in optionImages"
                                :key="`option-image-${index}`"
                                data-option
                                :data-selected="selectedAnswer.number === index  + 1"
                                :data-option-type="option.type"
                                :tabindex="editingAllowed ? 0 : -1"
                                @click="changeAnswerImage(option, selectedAnswer, index)"
                                @keydown.space="changeAnswerImage(option, selectedAnswer, index)"
                            >
                                <media
                                    v-if="option.type === `image`"
                                    type="Image"
                                    :ref="`option-image-${index}`"
                                    :id="`question-${questionData.number}_option-image-${index}`"
                                    :alt="option.alt"
                                    :url="`${state.surveyInfo.domain}/v1/${option.url}`"
                                    :disabled="!editingAllowed"
                                    :style="option.extension === `.svg` ? `min-width:50px;` : ``"
                                />
                                <div
                                    v-else-if="option.type === `text`"
                                    :disabled="!editingAllowed"
                                >
                                    <p v-html="DOMPurify.sanitize(option.label)"></p>
                                </div>
                            </div>
                        </div>
                        <label 
                            :for="`question-${questionData.number}-${selectedAnswerIndex}`"
                            tab-index="-1">
                            {{ $store.getters.textResource(`rs_give_an_explanation`) }}
                        </label>
                        <textarea
                            :id="`question-${questionData.number}-${selectedAnswerIndex}`"
                            v-model="selectedAnswer.userText"
                            ref="popup-text-input"
                            @keyup.enter.stop
                            maxlength="2250"
                        ></textarea>
                    </main>
                    <footer>
                        <navbutton
                            direction="right"
                            styling="secondary"
                            :isNavigation="false"
                            :text="$store.getters.textResource(`rs_accept`)"
                            @element-click="nextClicked"
                            id="next"
                        />
                    </footer>
                </div>
            </div>
        </transition>
        <!-- <font-awesome-icon :icon="['fas', 'arrow-up-right-and-arrow-down-left-from-center']" /> -->
        <questionfooter :questionData="questionData" />
        <validationpopup :questionValidation="questionValidation" />
    </div>
</template>

<script>
import mixins from "@/global/mixins";
import navbutton from "@/components/Navbutton";
import zoomImage from "@/components/ZoomImage";
//import methods from "@/global/methods.js";

export default {
    name: `hotspot-question`,
    mixins: [mixins.defaultQuestion],
    inject: ['DOMPurify'],
    components: {
        navbutton,
        zoomImage
    },
    data: function () {
        return {
            selectedImageIndex: -1,
            showTextPopup: false,
            selectedAnswerIndex: 0,
            forceRecomputeCounter: 0,
            showImages: false,
            imageWidth: 0,
            answerAmt: 0,
            imageTimeout: {},
            imageInterval: null,
            imageLoaded: false,
            position: ``,
            componentMounted: false,
        };
    },
    computed: {
        noAnswerIsSelected(){
            return this.selectedImageIndex === -1;
        },
        answersGiven() {
            return this.givenAnswers.length;
        },
        allAnswersGiven: function () {
            return this.answersGiven === this.questionData.subQuestions.length;
        },
        editingAllowed: function () {
            return this.get.editingAllowed(this.questionData);
        },
        mainImage: function () {
            return this.questionData.media[0];
        },
        subQuestions: function () {
            return this.questionData.subQuestions;
        },
        mainImageSize: function () {
            let mainImageRef = this.$refs[`main-image`];
            this.forceRecomputeCounter;

            if (mainImageRef === undefined) {
                return {
                    x: 0,
                    y: 0,
                };
            }

            let mainImageBCR = mainImageRef.$el.getBoundingClientRect();

            return {
                x: mainImageBCR.width,
                y: mainImageBCR.height,
            };
        },
        selectedAnswer: function () {
            if (this.answersGiven === 0) return [];
            else return this.givenAnswers[this.selectedAnswerIndex];
        },
        // At which subquestion are we currently?
        sqIndex: function () {
            return this.subQuestions.findIndex((sq) => {
                return sq.answers.find((answer) => answer.isChecked) === undefined;
            });
        },
        givenAnswers: function () {
            let checkedAnswers = [];

            this.subQuestions.forEach((sq) => {
                let answerArr = sq.answers.filter((answer) => answer.isChecked);

                checkedAnswers.push(...answerArr);
            });

            return checkedAnswers;
        },
        // Get a list of the answers that are possible in this question
        answers: function () {
            return this.questionData.subQuestions[0].answers;
        },
        // How high should the options container be.
        optionsHeight: function () {
            this.forceRecomputeCounter;

            let answer = this.answers[this.answers.length - 1];
            return answer.height + answer.y + 50;
        },
        // * Decide on the positions the option images. This is basically what display flex does but for images that are using position: absolute;
        optionImages: function () {
            // If the width hasn't yet been calculated, it's useless to do any other calculations about positioning
            if (this.imageWidth === 0) return;

            let answerY = 10; // 10 For some padding on top
            let rowGutter = 10;
            let colGutter = 10;
            let widthUsedThisRow = 0; // Width used on the current row
            let rowNumber = 0; // Current row number
            let rowWidths = []; // Store the width used per row

            this.answers.forEach((answer, index) => {
                let distanceToNewItem = answer.width + colGutter;
                
                // Do we have sufficient room for the next item?
                if (widthUsedThisRow + answer.width + colGutter <= this.imageWidth) {
                    answer.rowNumber = rowNumber;
                    answer.x = widthUsedThisRow;
                    widthUsedThisRow += distanceToNewItem;
                } else {
                    // Store the width used for the current row before moving to the next
                    rowWidths[rowNumber] = widthUsedThisRow - colGutter;

                    rowNumber++;
                    answer.rowNumber = rowNumber;
                    widthUsedThisRow = 0;
                    answer.x = widthUsedThisRow;
                    widthUsedThisRow += distanceToNewItem;
                }
                
                // If it's the last item, store the width used for the last row
                if (this.answers.length === index + 1) {
                    rowWidths[rowNumber] = widthUsedThisRow - colGutter;
                }
            });

            // Calculate the maximum row width to determine total content width
            let contentWidth = Math.max(...rowWidths);
            let horizontalOffset = (this.imageWidth - contentWidth) * 0.5;

            rowWidths.forEach((rowWidth, index) => {
                let answersOnThisRow = this.answers.filter((answer) => answer.rowNumber === index);

                // Calculate offset to center items within their row (if needed)
                let rowOffset = (contentWidth - rowWidth) * 0.5;

                answersOnThisRow.forEach((answer) => {
                    answer.x += horizontalOffset + rowOffset;
                    answer.y = answerY;
                });

                answerY += 50 + rowGutter;
            });

            return this.answers.map((answer) => {
                let returnObj = {
                    id: answer.id,
                    x: answer.x,
                    width: answer.width,
                    y: answer.y,
                    height: answer.initialHeight,
                    rowNumber: answer.rowNumber
                };

                if (answer.media.length === 0) {
                    returnObj.type = `text`;
                    returnObj.label = answer.label;
                    return returnObj;
                } else {
                    returnObj.type = `image`;
                    returnObj.url = answer.media[0].url;
                    returnObj.alt = answer.media[0].alt;
                    returnObj.extension = answer.media[0].url.substring(answer.media[0].url.lastIndexOf("."));
                    return returnObj;
                }
            });
        },
    },
    watch: {
        answers(newAnswers, oldAnswers) {
            if (!this.componentMounted) return;
            if (newAnswers === oldAnswers) return;
            if (!Array.isArray(newAnswers) || newAnswers.length === 0) return;

            this.resetOptionLayout();
        },
        mainImage(newImage, oldImage) {
            if (!this.componentMounted) return;

            const newUrl = newImage ? newImage.url : null;
            const oldUrl = oldImage ? oldImage.url : null;

            if (newUrl === oldUrl) return;

            this.resetOptionLayout();
        },
    },
    methods: {
        resetOptionLayout() {
            if (!this.componentMounted) return;

            this.showImages = false;

            if (!this.$refs[`main`]) {
                this.$nextTick(() => this.initializeQuestionLayout());
                return;
            }

            this.initializeQuestionLayout();
        },
        initializeQuestionLayout() {
            if (!this.$refs[`main`]) return;

            this.calculateValues();
            this.applyAnswerDimensions();
            this.forceRecomputeCounter++;
            this.restartImagePolling();
        },
        applyAnswerDimensions() {
            const answers = this.answers;

            if (!Array.isArray(answers) || answers.length === 0) return;

            const imageSize = 50;
            const letterWidth = 12;
            const lettersPerRow = 25;
            const rowHeight = 20;
            const padding = 15;

            // Calculate the required widths and heights of the answers
            answers.forEach((answer) => {
                if (answer.media.length > 0) {
                    answer.width = answer.initialHeight = imageSize;
                    answer.height = answer.initialWidth = imageSize;
                } else {
                    answer.width = answer.label.length * letterWidth + padding;
                    answer.height = Math.ceil(answer.label.length / lettersPerRow) * rowHeight;

                    if (answer.width > 200) answer.width = 200;
                    if (answer.width < 50) answer.width = 50;
                    if (answer.height < 50) answer.height = 50;

                    answer.initialWidth = answer.width;
                    answer.initialHeight = answer.height;
                }
            });
        },
        restartImagePolling() {
            if (this.imageInterval) {
                clearInterval(this.imageInterval);
                this.imageInterval = null;
            }

            this.imageLoaded = false;

            this.imageInterval = setInterval(() => {
                this.forceRecomputeCounter++;

                if (this.mainImageSize.x > 0) {
                    this.imageLoaded = true;
                    clearInterval(this.imageInterval);
                    this.imageInterval = null;
                }
            }, 100);
        },
        getInitialWidthOfAnswer: function (givenAnswer) {
            let answer = this.answers.find((answer) => answer.number === givenAnswer.number);
            return answer;
        },
        changeAnswerImage: function(optionChosen, currentAnswer, index) {
            const cloneAnswer = structuredClone(currentAnswer);  // eslint-disable-line

            // Get current open field object
            let currentOpenFieldStringObject = JSON.parse(currentAnswer.openField);
            currentOpenFieldStringObject.emotiontype = index + 1;
            currentOpenFieldStringObject.text = optionChosen.label;
            currentOpenFieldStringObject.xpercent = currentAnswer.percentX;
            currentOpenFieldStringObject.ypercent = currentAnswer.percentY;
            // Change current open field object to modified version.
            let edittedOpenFieldStringObject = JSON.stringify(currentOpenFieldStringObject);

            this.questionData.subQuestions[this.selectedAnswerIndex].answers.forEach((answer, answerIndex) => {
                if(answerIndex + 1 === index + 1) {              
                    answer.openField = edittedOpenFieldStringObject;
                    answer.percentX = cloneAnswer.percentX;
                    answer.percentY = cloneAnswer.percentY;
                    answer.userText = cloneAnswer.userText;
                    answer.isChecked = true;
                }
                else {
                    answer.openField = ``;
                    answer.percentX = 0;
                    answer.percentY = 0;
                    answer.userText = null;
                    answer.isChecked = false;
                }
            });
        },
        optionMouseUp: function (imageIndex, giveAnswer, e) {
            // The event objects can differ
            let isSpacebar = e.code === `Space` || (e.x === 0 && e.y === 0);
            // We want to prevent the page from scrolling down
            if (isSpacebar) e.preventDefault();

            if (giveAnswer === false || isSpacebar) this.selectedImageIndex = imageIndex;

            if (giveAnswer && isSpacebar) this.$refs[`main-image`].$el.click();
        },
        optionDragStop: function (imageIndex) {
            if (this.sqIndex < 0) return;
            
            this.selectedImageIndex = imageIndex;
            this.selectedAnswerIndex = this.answersGiven;

            const optionPercentage = this.getOptionPercentage(`option-image-${imageIndex}`);
            let selectedAnswer = this.subQuestions[this.sqIndex].answers[this.selectedImageIndex];

            if (this.optionIsWithinAcceptableRange(optionPercentage)) {
                selectedAnswer.isChecked = true;

                this.setCoords(selectedAnswer, {
                    x: optionPercentage.percentX,
                    y: optionPercentage.percentY,
                });

                this.showTextPopup = this.questionData.showTextBox;
                this.selectedImageIndex = -1;
                this.showImages = true;
                this.showPopup(true);
            } else {
                this.showImages = true;
                this.showPopup(false);
            }
        },
        givenAnswerDragStop: function (index) {
            let answer = this.givenAnswers[index];
            const optionPercentage = this.getOptionPercentage(`given-answer-drag-${index}`)
            const imageIsClicked = Math.round(optionPercentage.percentX) === Math.round(answer.percentX) && Math.round(optionPercentage.percentY) === Math.round(answer.percentY);

            
            if (imageIsClicked) {
                if (this.questionData.showTextBox === false) return;

                this.selectedAnswerIndex = index;

                this.$nextTick(() => {
                    if (optionPercentage.isChecked === false) return;

                    this.showPopup();
                });
            } else {
                if(this.optionIsWithinAcceptableRange(optionPercentage)) {
                    this.setCoords(answer, {
                        x: optionPercentage.percentX,
                        y: optionPercentage.percentY,
                    });
                } else {
                    answer.percentX = parseInt(answer.initialPercentX)
                    answer.percentY = parseInt(answer.initialPercentY)
                }
            }
        },
        optionIsWithinAcceptableRange(optionPercentage) {
            return optionPercentage.percentX >= 0 && 
                optionPercentage.percentX <= 100 && 
                optionPercentage.percentY >= 0 && 
                optionPercentage.percentY <= 100;
        },
        getOptionPercentage(imageRefName) {
            const mainImage = this.$refs[`main-image`].$el.getBoundingClientRect();
            const draggedImage = this.$refs[imageRefName][0].$el.firstChild.getBoundingClientRect();
            const centerX = draggedImage.left + draggedImage.width / 2;
            const centerY = draggedImage.top + draggedImage.height / 2;
            const relativeCenterX = centerX - mainImage.left;
            const relativeCenterY = centerY - mainImage.top;
            const percentX = (relativeCenterX / mainImage.width) * 100;
            const percentY = (relativeCenterY / mainImage.height) * 100;
            
            return {
                percentX,
                percentY
            }
        },
        nextClicked: function () {
            let ref = this.$refs[`given-answer-${this.selectedAnswerIndex}`][0];

            this.showTextPopup = false;
            this.setCoords(this.selectedAnswer, {});

            // Focus on the . The location of element to click can change because this function can be called in different ways
            if (ref && ref.$el) ref.$el.focus();
            else if (ref) ref.focus();
        },
        removeGivenAnswer: function (index) {
            let answer = this.givenAnswers[index];
            answer.isChecked = false;
            answer.openField = ``;
            answer.userText = ``;

            this.showPopup(false);
        },
        mainImageMouseUp: function (e) {
            if (this.selectedImageIndex !== -1) {
                let selectedAnswer = this.subQuestions[this.sqIndex].answers[this.selectedImageIndex];
                let percentX, percentY, x, y;

                this.selectedAnswerIndex = this.sqIndex;

                // If this is triggered by a spacebar
                if (e.screenX === 0 && e.screenY === 0) {
                    percentX = 50;
                    percentY = 50;
                }
                // If this is triggered by a click or mouse
                else {
                    let imageBCR = e.target.getBoundingClientRect();
                    let clientX, clientY;

                    // Is the event triggered by mouse- or touchevent?
                    if (e.clientY) {
                        clientX = e.clientX;
                        clientY = e.clientY;
                    } else if (e.changedTouches) {
                        clientX = e.changedTouches[0].clientX;
                        clientY = e.changedTouches[0].clientY;
                    }

                    x = clientX - imageBCR.left;
                    y = clientY - imageBCR.top;
                    percentX = (x / imageBCR.width) * 100;
                    percentY = (y / imageBCR.height) * 100;
                }

                selectedAnswer.isChecked = true;
                this.setCoords(selectedAnswer, {
                    x: percentX,
                    y: percentY,
                });

                this.selectedImageIndex = -1;

                this.showPopup();
            }
        },
        // Set the coordinates of the answer and generate the openField property which is used by the backend to properly process the information
        setCoords: function (answer, percent) {
            let percentX = percent.x ?? answer.percentX;
            let percentY = percent.y ?? answer.percentY;
            
            const answerText = answer.userText || answer.label;

            if (percentX > 100) percentX = 100;
            if (percentX < 0) percentX = 0;
            if (percentY > 100) percentY = 100;
            if (percentY < 0) percentY = 0;
            
            answer.openField = JSON.stringify({
                id: this.selectedAnswerIndex,
                x: 0,
                xpercent: percentX,
                y: 0,
                ypercent: percentY,
                offsetx: 0,
                offsety: 0,
                emotiontype: answer.number,
                text: answerText, 
                mandatory: 0,
            });
            
            answer.percentX = percentX;
            answer.percentY = percentY;
        },
        givenAnswerKeyUp: function (e, index) {
            let answer = this.givenAnswers[index];
            switch (e.code) {
                case `Space`:
                    e.preventDefault();
                    this.givenAnswerMouseUp(index);
                    break;
                case `ArrowLeft`:
                    e.preventDefault();
                    this.setCoords(answer, {
                        x: (answer.percentX -= 1),
                    });
                    break;
                case `ArrowRight`:
                    e.preventDefault();
                    this.setCoords(answer, {
                        x: (answer.percentX += 1),
                    });
                    break;
                case `ArrowUp`:
                    e.preventDefault();
                    this.setCoords(answer, {
                        y: (answer.percentY -= 1),
                    });
                    break;
                case `ArrowDown`:
                    e.preventDefault();
                    this.setCoords(answer, {
                        y: (answer.percentY += 1),
                    });
                    break;
            }
        },
        givenAnswerMouseUp: function (index) {
            if (this.questionData.showTextBox === false) return;

            this.selectedAnswerIndex = index;

            this.showPopup();
        },
        givenAnswerDragStart: function(index) {
            const answer = this.givenAnswers[index];
            answer.initialPercentX = answer.percentX;
            answer.initialPercentY = answer.percentY;
        },
        showPopup(show) {
            this.showTextPopup = show === false ? false : this.questionData.showTextBox;

            this.$nextTick(() => {
                // Reset the images on top
                this.showImages = false;

                if (this.showTextPopup === false) return;
                // Timeout needed because the popup has to be initiated first
                this.$refs[`popup-text-input`].focus();
            });
        },
        onResize() {
            clearTimeout(this.resizedFinished);

            // Resize debouncer
            this.resizedFinished = setTimeout(() => {
                this.calculateValues();

                // For some stupid reason we have to trigger some computed properties
                this.$nextTick(() => {
                    this.forceRecomputeCounter++;
                });
            }, 250);
        },
        // Recalculate some value so that the positioning of options is improved
        calculateValues() {
            this.imageWidth = this.$refs[`main`].getBoundingClientRect().width - 50;
            this.answerAmt = this.answers.length;
        },
    },
    beforeDestroy: function () {
        window.removeEventListener("resize", this.onResize);

        if (this.imageInterval) {
            clearInterval(this.imageInterval);
            this.imageInterval = null;
        }
    },
    mounted: function () {
        window.addEventListener("resize", this.onResize);
        this.componentMounted = true;
        this.resetOptionLayout();
    },
};
</script>

<style scoped>
@import "./../styles/variables.css";

[data-image] {
    display: flex;
    justify-content: center;
}

[data-option-type="text"] {
    display: flex;
    justify-content: center;
    align-items: center;
}

[data-tab-given-answer] {
    min-width: 55px;
    min-height: 55px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
        margin: 0;
    }

    > p {
        font-size: 14px;
        min-height: 50px;
        display: flex;
        justify-content: center;
        align-items: center;
        background: white;
        border: 1px solid #333;
        padding: 10px;
        margin: 0;
        user-select: none;
        border-radius: 5px;
        width: 100%;
        position: absolute;
        text-align: center;
    }

    [data-remove-given-answer] {
        border: 1px solid;
        position: absolute;
        right: -15px;
        top: -15px;
        border-radius: 50%;
        width: 26px;
        background: #fff;
        height: 26px;
        display: flex;
        justify-content: center;
        align-items: center;
        transition: 0.1s;

        &:hover {
            transform: scale(1.1);
        }
    }
}

.vdr {
    display: block;
    transform: translate(-50%, -50%);

    p {
        cursor: pointer;
        margin: 0;
    }

    &.active:before {
        position: absolute;
        outline: 0 !important; /* ! Overwwrite the drag and drop plugin */
    }

    &:before {
        outline: 0 !important; /* ! Overwwrite the drag and drop plugin */
    }
}

[data-drag-option-image] {
    > div {
        pointer-events: none;
        border: 1px solid;
        border-radius: 3px;
        max-width: 200px;
        text-align: center;
        background: white;
        height: 100%;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;

        p {
            padding: 8px 10px;
            font-size: 14px;
            margin: 0;
            text-align: center;
        }
    }
}

[data-image-area] {
    position: relative;
    margin: 0;

    [data-drag-option-image] p {
        pointer-events: none;
        margin: 0;
        font-size: 14px;
        border: 1px solid;
        border-radius: 3px;
        max-width: 200px;
        text-align: center;
        padding: 15px;
        width: 100%;
        background: white;
    }
}

[data-remove-popup] {
    position: absolute;
    right: 15px;
    top: 12px;
    cursor: pointer;
    transition: 0.1s;
    font-size: 20px;

    &:hover {
        transform: scale(1.1);
    }
}

[data-text-popup] {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(69, 72, 72, 0.5);
    padding: 30px 20px;
    z-index: 1111;
    display: flex;
    justify-content: center;
}

[data-prompt] {
    width: calc(100% - 40px);
    background: #fff;
    border-radius: 5px;
    bottom: 30px;
    position: absolute;
    max-width: 800px;

    header {
        padding: 20px 20px;
    }

    main {
        padding: 20px;
        background: #fff;
        label {
            padding-block-end: 10px;
        }
        textarea {
            height: 125px;
        }
    }

    footer {
        padding: 20px;
        display: flex;
        flex-direction: row;
        justify-content: center;

        button {
            margin: 0 8px;
        }
    }
}

[data-question-main] {
    background: white;

    [data-options] {
        position: sticky;
        width: 100%;
        z-index: 11;
        background: white;
        top: 0;

        .vdr {
            transform: translate(0, 0);
        }
    }
}

img {
    cursor: pointer;
    user-select: none;
    margin: 0;
    border-radius: 3px;

    &:after {
        content: "";
        width: 30px;
        height: 30px;
        border: 1px solid;
        background: red;
    }
}

[data-option] {
    cursor: pointer;
    outline: 0;

    .vdr {
        max-width: 200px;
        display: flex;
        justify-content: center;
        align-items: flex-start;

        &[disabled] {
            background: transparent;
            pointer-events: none;

            div {
                background: #eaeaea;
                color: gray;
            }
        }
    }

    &:hover,
    &:focus,
    &[data-selected] {
        > div[data-option-type="text"],
        &[data-option-type="text"],
        img {
            outline: 2px solid var(--primary-color);
            border-radius: 3px;
            border: 0;
            > div {
                border: 0;
            }
        }
    }
}

div[option-images-container] {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    padding-block-end: 15px;
    gap: 10px;
    [data-option-type="image"] {
        width: 50px;
        height: 50px;
    }
    [data-option-type="text"] {
        min-width: 125px;
        height: 50px;
        padding: 0 10px; 
        border: 1px solid;
        border-radius: 3px;
        > div {
            display: flex;
            justify-content: center;
        }
    }
}

@media only screen and (max-width: 550px) {
    body [question] div[data-question-main] {
        padding: 15px 25px;
    }
}
</style>